/**
 * Enterprise Penetration Testing Module Component
 * Pentest Report Import & Vulnerability Tracking
 */

import React, { useState, useEffect } from 'react';
import { useI18n } from '../../context/I18nContext';
import { pentestService } from '../../services/enterpriseService';
import { grcService } from '../../services/grcService';
import { Modal, FileUpload, ControlSelector, useEvidenceIntegration } from './SharedModals';
import {
  Bug, FileText, Upload, AlertTriangle, CheckCircle, XCircle,
  RefreshCw, Search, Filter, Eye, Clock, Target, Shield, Calendar,
  Download, ExternalLink, TrendingDown, BarChart2, LinkIcon, X, Image, Camera
} from 'lucide-react';

interface PentestReport {
  id: number;
  title: string;
  vendor_name: string;
  test_type: 'external' | 'internal' | 'web_app' | 'mobile' | 'api' | 'social_engineering';
  scope_description?: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'remediation';
  start_date?: string;
  end_date?: string;
  report_date?: string;
  risk_rating: 'critical' | 'high' | 'medium' | 'low';
  findings_critical: number;
  findings_high: number;
  findings_medium: number;
  findings_low: number;
  findings_info: number;
  created_at: string;
}

interface PentestFinding {
  id: number;
  report_id: number;
  title: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  cvss_score?: number;
  cvss_vector?: string;
  cwe_id?: string;
  cve_ids?: string;
  owasp_category?: string;
  description?: string;
  affected_systems?: string;
  affected_url?: string;
  proof_of_concept?: string;
  reproduction_steps?: string;
  remediation?: string;
  remediation_effort?: string;
  status: 'open' | 'in_progress' | 'resolved' | 'accepted_risk' | 'false_positive';
  due_date?: string;
  assigned_to?: number;
  linked_control_id?: number;
}

export const EnterprisePentest: React.FC<{ frameworkName?: string }> = ({ frameworkName }) => {
  const { language } = useI18n();
  const [reports, setReports] = useState<PentestReport[]>([]);
  const [findings, setFindings] = useState<PentestFinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'reports' | 'findings'>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReport, setSelectedReport] = useState<number | null>(null);

  // Modal states
  const [showImportModal, setShowImportModal] = useState(false);
  const [showLinkControlsModal, setShowLinkControlsModal] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState<PentestFinding | null>(null);
  const [saving, setSaving] = useState(false);

  // Import form state
  const [importForm, setImportForm] = useState({
    title: '',
    vendor_name: '',
    test_type: 'external' as PentestReport['test_type'],
    scope_description: '',
    file: null as File | null
  });

  // Control linking
  const [frameworks, setFrameworks] = useState<any[]>([]);
  const [selectedFramework, setSelectedFramework] = useState<number | null>(null);
  const [selectedControls, setSelectedControls] = useState<number[]>([]);
  const { linkPentestToControl } = useEvidenceIntegration();

  // Finding detail modal
  const [showFindingDetail, setShowFindingDetail] = useState(false);
  const [detailFinding, setDetailFinding] = useState<PentestFinding | null>(null);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reportsData, findingsData] = await Promise.all([
        pentestService.getReports(),
        pentestService.getFindings().catch(() => ({ findings: [] }))
      ]);
      
      // Handle API response format
      const reportsList = reportsData?.data || reportsData?.reports || (Array.isArray(reportsData) ? reportsData : []);
      setReports(reportsList);
      const findingsList = findingsData?.data || findingsData?.findings || (Array.isArray(findingsData) ? findingsData : []);
      setFindings(findingsList);
    } catch (error) {
      console.error('Error loading pentest data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFrameworks = async () => {
    try {
      const data = await grcService.getFrameworks();
      const frameworkList = Array.isArray(data) ? data : ((data as any)?.frameworks || []);
      setFrameworks(frameworkList);
    } catch (error) {
      console.error('Error loading frameworks:', error);
    }
  };

  const handleImportReport = async () => {
    if (!importForm.title || !importForm.vendor_name) return;
    setSaving(true);
    try {
      await pentestService.uploadReport({
        title: importForm.title,
        vendor_name: importForm.vendor_name,
        test_type: importForm.test_type,
        scope_description: importForm.scope_description,
        file: importForm.file
      });
      setShowImportModal(false);
      setImportForm({ title: '', vendor_name: '', test_type: 'external', scope_description: '', file: null });
      loadData();
    } catch (error) {
      console.error('Error importing report:', error);
    } finally {
      setSaving(false);
    }
  };

  const openLinkModal = (finding: PentestFinding) => {
    setSelectedFinding(finding);
    setSelectedControls([]);
    setSelectedFramework(null);
    loadFrameworks();
    setShowLinkControlsModal(true);
  };

  const handleLinkToControls = async () => {
    if (!selectedFinding || selectedControls.length === 0) return;
    setSaving(true);
    try {
      for (const controlId of selectedControls) {
        await linkPentestToControl(
          controlId,
          selectedFinding.id,
          selectedFinding.title,
          selectedFinding.severity
        );
      }
      setShowLinkControlsModal(false);
      setSelectedFinding(null);
    } catch (error) {
      console.error('Error linking to controls:', error);
    } finally {
      setSaving(false);
    }
  };

  const openFindingDetail = async (finding: PentestFinding) => {
    try {
      const full = await pentestService.getFinding(finding.id);
      setDetailFinding(full || finding);
    } catch {
      setDetailFinding(finding);
    }
    setShowFindingDetail(true);
  };

  const handleEvidenceUpload = async (findingId: number) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      setUploadingEvidence(true);
      try {
        await pentestService.uploadEvidenceImage(findingId, file, true);
        // Reload detail
        const full = await pentestService.getFinding(findingId);
        setDetailFinding(full);
      } catch (err) {
        console.error('Error uploading evidence:', err);
      } finally {
        setUploadingEvidence(false);
      }
    };
    input.click();
  };

  const handleUpdateFindingStatus = async (findingId: number, status: string) => {
    try {
      await pentestService.updateFindingStatus(findingId, status);
      loadData();
      if (detailFinding && detailFinding.id === findingId) {
        setDetailFinding({ ...detailFinding, status: status as PentestFinding['status'] });
      }
    } catch (err) {
      console.error('Error updating finding status:', err);
    }
  };

  const renderEvidenceContent = (text: string) => {
    if (!text) return null;
    const parts = text.split(/(\[Image Evidence: [^\]]+\])/g);
    return parts.map((part, i) => {
      const match = part.match(/\[Image Evidence: ([^\]]+)\]/);
      if (match) {
        const imgPath = match[1];
        return (
          <div key={i} className="my-2">
            <img
              src={imgPath}
              alt="Evidence"
              className="max-w-full rounded-lg border border-gray-300 dark:border-gray-600 max-h-96 object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
        );
      }
      return part ? <span key={i} className="whitespace-pre-wrap">{part}</span> : null;
    });
  };

  const testTypes = {
    external: { label: language === 'sr' ? 'Vanjski' : 'External', color: 'blue' },
    internal: { label: language === 'sr' ? 'Interni' : 'Internal', color: 'purple' },
    web_app: { label: 'Web App', color: 'cyan' },
    mobile: { label: 'Mobile', color: 'green' },
    api: { label: 'API', color: 'orange' },
    social_engineering: { label: 'Social Eng.', color: 'red' }
  };

  const severityColors = {
    critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    info: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
  };

  const statusColors = {
    scheduled: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    remediation: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
  };

  const openFindings = findings.filter(f => f.status === 'open' || f.status === 'in_progress');
  const criticalFindings = openFindings.filter(f => f.severity === 'critical');
  const highFindings = openFindings.filter(f => f.severity === 'high');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Bug className="w-7 h-7 text-red-500" />
            {language === 'sr' ? 'Testiranje prodora' : 'Penetration Testing'}
            {frameworkName && (
              <span className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full text-sm font-medium">
                {frameworkName}
              </span>
            )}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {language === 'sr' 
              ? 'Upravljajte pentest izvještajima i ranjivostima'
              : 'Manage pentest reports and vulnerability tracking'
            }
          </p>
        </div>
        <button 
          onClick={() => setShowImportModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Upload className="w-4 h-4" />
          {language === 'sr' ? 'Uvezi izvještaj' : 'Import Report'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <FileText className="w-8 h-8 text-indigo-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{reports.length}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Izvještaja' : 'Reports'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{criticalFindings.length}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Kritično' : 'Critical'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <Target className="w-8 h-8 text-orange-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{highFindings.length}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Visoko' : 'High'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <Bug className="w-8 h-8 text-yellow-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{openFindings.length}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Otvoreno' : 'Open'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {findings.filter(f => f.status === 'resolved').length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Riješeno' : 'Resolved'}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {[
          { key: 'overview', icon: BarChart2, label: language === 'sr' ? 'Pregled' : 'Overview' },
          { key: 'reports', icon: FileText, label: language === 'sr' ? 'Izvještaji' : 'Reports' },
          { key: 'findings', icon: Bug, label: language === 'sr' ? 'Nalazi' : 'Findings' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as typeof activeTab)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-red-500 text-red-600 dark:text-red-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.key === 'findings' && openFindings.length > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded-full text-xs">
                {openFindings.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Critical Findings */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              {language === 'sr' ? 'Kritični nalazi' : 'Critical Findings'}
            </h3>
            <div className="space-y-3">
              {[...criticalFindings, ...highFindings].slice(0, 5).map(finding => (
                <div key={finding.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${severityColors[finding.severity]}`}>
                          {finding.severity.toUpperCase()}
                        </span>
                        {finding.cvss_score && (
                          <span className="text-xs text-gray-500">CVSS: {finding.cvss_score}</span>
                        )}
                      </div>
                      <div className="font-medium text-gray-900 dark:text-white mt-1">{finding.title}</div>
                      <div className="text-xs text-gray-500 mt-1">{finding.affected_systems}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => openLinkModal(finding)}
                        className="p-1 text-indigo-400 hover:text-indigo-600" 
                        title={language === 'sr' ? 'Poveži sa kontrolama' : 'Link to controls'}
                      >
                        <LinkIcon className="w-4 h-4" />
                      </button>
                      <button className="p-1 text-gray-400 hover:text-gray-600">
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {criticalFindings.length === 0 && highFindings.length === 0 && (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p>{language === 'sr' ? 'Nema kritičnih nalaza' : 'No critical findings'}</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Reports */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-500" />
              {language === 'sr' ? 'Nedavni izvještaji' : 'Recent Reports'}
            </h3>
            <div className="space-y-3">
              {reports.slice(0, 5).map(report => (
                <div key={report.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{report.title}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded text-xs ${severityColors[report.risk_rating]}`}>
                          {report.risk_rating}
                        </span>
                        <span className="text-xs text-gray-500">{report.vendor_name}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {report.findings_critical + report.findings_high + report.findings_medium} {language === 'sr' ? 'nalaza' : 'findings'}
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${statusColors[report.status]}`}>
                      {report.status}
                    </span>
                  </div>
                </div>
              ))}
              {reports.length === 0 && (
                <p className="text-center py-4 text-gray-500 dark:text-gray-400">
                  {language === 'sr' ? 'Nema izvještaja' : 'No reports'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={language === 'sr' ? 'Pretraži izvještaje...' : 'Search reports...'}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
              <Filter className="w-4 h-4" />
              {language === 'sr' ? 'Filter' : 'Filter'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reports.map(report => (
              <div key={report.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">{report.title}</h4>
                    <span className="text-sm text-gray-500">{report.vendor_name}</span>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${statusColors[report.status]}`}>
                    {report.status}
                  </span>
                </div>
                
                <div className="flex items-center gap-2 mb-4">
                  <span className={`px-2 py-1 rounded text-xs bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`}>
                    {testTypes[report.test_type]?.label}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${severityColors[report.risk_rating]}`}>
                    {report.risk_rating} risk
                  </span>
                </div>
                
                {/* Findings Summary */}
                <div className="flex items-center gap-2 mb-4 text-sm">
                  {report.findings_critical > 0 && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded">
                      {report.findings_critical} C
                    </span>
                  )}
                  {report.findings_high > 0 && (
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 rounded">
                      {report.findings_high} H
                    </span>
                  )}
                  {report.findings_medium > 0 && (
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 rounded">
                      {report.findings_medium} M
                    </span>
                  )}
                  {report.findings_low > 0 && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded">
                      {report.findings_low} L
                    </span>
                  )}
                </div>
                
                <div className="flex items-center gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <button className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-indigo-700 bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 rounded-lg hover:bg-indigo-200">
                    <Eye className="w-4 h-4" />
                    {language === 'sr' ? 'Pregledaj' : 'View'}
                  </button>
                  <button className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Findings Tab */}
      {activeTab === 'findings' && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Ozbiljnost' : 'Severity'}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Naziv' : 'Title'}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">CVSS</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Status' : 'Status'}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Rok' : 'Due Date'}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Akcije' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {findings.map(finding => (
                  <tr key={finding.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => openFindingDetail(finding)}>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${severityColors[finding.severity]}`}>
                        {finding.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{finding.title}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{finding.cvss_score || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      <select
                        value={finding.status}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleUpdateFindingStatus(finding.id, e.target.value)}
                        className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="remediated">Remediated</option>
                        <option value="accepted">Accepted Risk</option>
                        <option value="false_positive">False Positive</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {finding.due_date ? new Date(finding.due_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button 
                          onClick={() => openFindingDetail(finding)} 
                          className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded" 
                          title={language === 'sr' ? 'Detalji' : 'Details'}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => openLinkModal(finding)} 
                          className="p-1 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded" 
                          title={language === 'sr' ? 'Poveži' : 'Link'}
                        >
                          <LinkIcon className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleEvidenceUpload(finding.id)} 
                          className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded" 
                          title={language === 'sr' ? 'Dodaj sliku' : 'Upload Image'}
                        >
                          <Camera className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {findings.length === 0 && (
              <p className="text-center py-8 text-gray-500 dark:text-gray-400">
                {language === 'sr' ? 'Nema nalaza' : 'No findings'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Import Report Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title={language === 'sr' ? 'Uvezi pentest izvještaj' : 'Import Pentest Report'}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowImportModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {language === 'sr' ? 'Otkaži' : 'Cancel'}
            </button>
            <button
              onClick={handleImportReport}
              disabled={saving || !importForm.title || !importForm.vendor_name}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {saving ? (language === 'sr' ? 'Uvoz...' : 'Importing...') : (language === 'sr' ? 'Uvezi' : 'Import')}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Naziv izvještaja *' : 'Report Title *'}
            </label>
            <input
              type="text"
              value={importForm.title}
              onChange={(e) => setImportForm({...importForm, title: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Q4 2024 External Penetration Test"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Izvođač *' : 'Vendor Name *'}
            </label>
            <input
              type="text"
              value={importForm.vendor_name}
              onChange={(e) => setImportForm({...importForm, vendor_name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="SecureTest Inc."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Tip testa' : 'Test Type'}
            </label>
            <select
              value={importForm.test_type}
              onChange={(e) => setImportForm({...importForm, test_type: e.target.value as PentestReport['test_type']})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="external">{language === 'sr' ? 'Vanjski' : 'External'}</option>
              <option value="internal">{language === 'sr' ? 'Interni' : 'Internal'}</option>
              <option value="web_app">Web App</option>
              <option value="mobile">Mobile</option>
              <option value="api">API</option>
              <option value="social_engineering">Social Engineering</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Opis opsega' : 'Scope Description'}
            </label>
            <textarea
              value={importForm.scope_description}
              onChange={(e) => setImportForm({...importForm, scope_description: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder={language === 'sr' ? 'Opišite opseg testiranja...' : 'Describe the testing scope...'}
            />
          </div>
          <FileUpload
            accept=".pdf,.docx,.xml,.json,.html"
            onFileSelect={(file) => setImportForm({...importForm, file})}
            label={language === 'sr' ? 'Izvještaj (PDF, DOCX, XML, JSON, HTML)' : 'Report File (PDF, DOCX, XML, JSON, HTML)'}
          />
        </div>
      </Modal>

      {/* Finding Detail Modal */}
      <Modal
        isOpen={showFindingDetail}
        onClose={() => { setShowFindingDetail(false); setDetailFinding(null); }}
        title={detailFinding?.title || (language === 'sr' ? 'Detalji nalaza' : 'Finding Details')}
        footer={
          <div className="flex justify-between w-full">
            <button
              onClick={() => detailFinding && handleEvidenceUpload(detailFinding.id)}
              disabled={uploadingEvidence}
              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm disabled:opacity-50"
            >
              <Camera className="w-4 h-4" />
              {uploadingEvidence 
                ? (language === 'sr' ? 'Otpremanje...' : 'Uploading...') 
                : (language === 'sr' ? 'Dodaj sliku' : 'Upload Evidence')}
            </button>
            <button
              onClick={() => { setShowFindingDetail(false); setDetailFinding(null); }}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {language === 'sr' ? 'Zatvori' : 'Close'}
            </button>
          </div>
        }
      >
        {detailFinding && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Severity badge + CVSS */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${severityColors[detailFinding.severity]}`}>
                {detailFinding.severity?.toUpperCase()}
              </span>
              {detailFinding.cvss_score && (
                <span className="text-sm text-gray-500 dark:text-gray-400">CVSS: {detailFinding.cvss_score}</span>
              )}
              {detailFinding.cvss_vector && (
                <span className="text-xs text-gray-400 font-mono">{detailFinding.cvss_vector}</span>
              )}
              {detailFinding.cwe_id && (
                <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">CWE-{detailFinding.cwe_id}</span>
              )}
              {detailFinding.owasp_category && (
                <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 px-2 py-0.5 rounded">{detailFinding.owasp_category}</span>
              )}
            </div>

            {/* Description */}
            {detailFinding.description && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  {language === 'sr' ? 'Opis' : 'Description'}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{detailFinding.description}</p>
              </div>
            )}

            {/* Affected URL/Assets */}
            {(detailFinding.affected_url || detailFinding.affected_systems) && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  {language === 'sr' ? 'Pogođeni sistemi' : 'Affected Systems'}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                  {detailFinding.affected_url || detailFinding.affected_systems}
                </p>
              </div>
            )}

            {/* Proof of Concept + Evidence Images */}
            {detailFinding.proof_of_concept && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  {language === 'sr' ? 'Dokaz (PoC)' : 'Proof of Concept'}
                </h4>
                <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                  {renderEvidenceContent(detailFinding.proof_of_concept)}
                </div>
              </div>
            )}

            {/* Reproduction Steps */}
            {detailFinding.reproduction_steps && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  {language === 'sr' ? 'Koraci reprodukcije' : 'Reproduction Steps'}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{detailFinding.reproduction_steps}</p>
              </div>
            )}

            {/* Remediation */}
            {detailFinding.remediation && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  {language === 'sr' ? 'Preporuka za otklanjanje' : 'Remediation'}
                </h4>
                <div className="text-sm text-gray-600 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                  <p className="whitespace-pre-wrap">{detailFinding.remediation}</p>
                  {detailFinding.remediation_effort && (
                    <span className="mt-2 inline-block text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 px-2 py-0.5 rounded">
                      {language === 'sr' ? 'Napor' : 'Effort'}: {detailFinding.remediation_effort}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Status */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                {language === 'sr' ? 'Status' : 'Status'}:
              </span>
              <select
                value={detailFinding.status}
                onChange={(e) => handleUpdateFindingStatus(detailFinding.id, e.target.value)}
                className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="remediated">Remediated</option>
                <option value="accepted">Accepted Risk</option>
                <option value="false_positive">False Positive</option>
              </select>
            </div>
          </div>
        )}
      </Modal>

      {/* Link to Controls Modal */}
      <Modal
        isOpen={showLinkControlsModal}
        onClose={() => setShowLinkControlsModal(false)}
        title={language === 'sr' ? 'Poveži nalaz sa kontrolama' : 'Link Finding to Controls'}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowLinkControlsModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {language === 'sr' ? 'Otkaži' : 'Cancel'}
            </button>
            <button
              onClick={handleLinkToControls}
              disabled={saving || selectedControls.length === 0}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? (language === 'sr' ? 'Čuvanje...' : 'Saving...') : (language === 'sr' ? 'Poveži' : 'Link')}
            </button>
          </div>
        }
      >
        {selectedFinding && (
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 rounded text-xs ${severityColors[selectedFinding.severity]}`}>
                  {selectedFinding.severity.toUpperCase()}
                </span>
                {selectedFinding.cvss_score && (
                  <span className="text-xs text-gray-500">CVSS: {selectedFinding.cvss_score}</span>
                )}
              </div>
              <div className="font-medium text-gray-900 dark:text-white">{selectedFinding.title}</div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {language === 'sr' 
                ? 'Ovaj nalaz će biti dodat kao dokaz za odabrane kontrole'
                : 'This finding will be added as evidence to selected controls'
              }
            </p>
            <ControlSelector
              frameworks={frameworks}
              selectedFramework={selectedFramework}
              onSelectFramework={setSelectedFramework}
              selectedControls={selectedControls}
              onSelectControls={setSelectedControls}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EnterprisePentest;
